import { Controller } from "@hotwired/stimulus"

// 사용자 친화적 날짜 선택 모달 컨트롤러
export default class extends Controller {
  static targets = ["modal", "input", "display", "calendar", "yearSelect", "monthSelect"]
  static values = {
    format: { type: String, default: "yyyy-MM-dd" },
    minDate: String,
    maxDate: String
  }

  connect() {
    this.currentDate = new Date()
    this.selectedDate = this.inputTarget.value ? new Date(this.inputTarget.value) : null
    this.viewYear = this.selectedDate ? this.selectedDate.getFullYear() : this.currentDate.getFullYear()
    this.viewMonth = this.selectedDate ? this.selectedDate.getMonth() : this.currentDate.getMonth()

    this.updateDisplay()
  }

  openModal(event) {
    event.preventDefault()

    // 모달이 없으면 생성
    if (!this.hasModalTarget) {
      this.createModal()
    }

    this.renderCalendar()
    this.modalTarget.showModal()
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.close()
    }
  }

  createModal() {
    const modal = document.createElement('dialog')
    modal.className = 'modal'
    modal.dataset.datePickerModalTarget = 'modal'
    modal.innerHTML = `
      <div class="modal-box max-w-sm p-0" data-action="click->date-picker-modal#stopPropagation">
        <!-- 헤더: 년/월 선택 -->
        <div class="flex items-center justify-between p-4 border-b border-gray-100">
          <button type="button" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100" data-action="click->date-picker-modal#prevMonth">
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <div class="flex items-center gap-2">
            <select data-date-picker-modal-target="yearSelect" data-action="change->date-picker-modal#changeYear" class="native-select text-base font-semibold border-none bg-transparent focus:ring-0 px-2 py-1">
            </select>
            <select data-date-picker-modal-target="monthSelect" data-action="change->date-picker-modal#changeMonth" class="native-select text-base font-semibold border-none bg-transparent focus:ring-0 px-2 py-1">
            </select>
          </div>
          <button type="button" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100" data-action="click->date-picker-modal#nextMonth">
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>

        <!-- 바로가기 버튼 -->
        <div class="flex gap-2 p-4 border-b border-gray-100">
          <button type="button" class="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100" data-action="click->date-picker-modal#selectToday">
            오늘
          </button>
          <button type="button" class="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200" data-action="click->date-picker-modal#selectTomorrow">
            내일
          </button>
          <button type="button" class="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200" data-action="click->date-picker-modal#selectDayAfter">
            모레
          </button>
        </div>

        <!-- 요일 헤더 -->
        <div class="grid grid-cols-7 px-4 pt-3">
          <div class="text-center text-xs font-medium text-red-500 py-2">일</div>
          <div class="text-center text-xs font-medium text-gray-500 py-2">월</div>
          <div class="text-center text-xs font-medium text-gray-500 py-2">화</div>
          <div class="text-center text-xs font-medium text-gray-500 py-2">수</div>
          <div class="text-center text-xs font-medium text-gray-500 py-2">목</div>
          <div class="text-center text-xs font-medium text-gray-500 py-2">금</div>
          <div class="text-center text-xs font-medium text-blue-500 py-2">토</div>
        </div>

        <!-- 캘린더 그리드 -->
        <div data-date-picker-modal-target="calendar" class="grid grid-cols-7 gap-1 p-4">
        </div>

        <!-- 푸터 -->
        <div class="flex items-center justify-between p-4 border-t border-gray-100">
          <button type="button" class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900" data-action="click->date-picker-modal#clearDate">
            초기화
          </button>
          <button type="button" class="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700" data-action="click->date-picker-modal#closeModal">
            닫기
          </button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button type="button" data-action="click->date-picker-modal#closeModal">닫기</button>
      </form>
    `

    this.element.appendChild(modal)
    this.populateYearSelect()
    this.populateMonthSelect()
  }

  populateYearSelect() {
    const select = this.yearSelectTarget
    const currentYear = new Date().getFullYear()

    select.innerHTML = ''
    for (let year = currentYear - 5; year <= currentYear + 5; year++) {
      const option = document.createElement('option')
      option.value = year
      option.textContent = `${year}년`
      option.selected = year === this.viewYear
      select.appendChild(option)
    }
  }

  populateMonthSelect() {
    const select = this.monthSelectTarget
    const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월']

    select.innerHTML = ''
    months.forEach((month, index) => {
      const option = document.createElement('option')
      option.value = index
      option.textContent = month
      option.selected = index === this.viewMonth
      select.appendChild(option)
    })
  }

  renderCalendar() {
    const calendar = this.calendarTarget

    // 년/월 선택 업데이트
    if (this.hasYearSelectTarget) {
      this.yearSelectTarget.value = this.viewYear
    }
    if (this.hasMonthSelectTarget) {
      this.monthSelectTarget.value = this.viewMonth
    }

    // 월의 첫날과 마지막 날
    const firstDay = new Date(this.viewYear, this.viewMonth, 1)
    const lastDay = new Date(this.viewYear, this.viewMonth + 1, 0)

    // 이전 달의 날짜들
    const prevMonthDays = firstDay.getDay()
    const prevMonth = new Date(this.viewYear, this.viewMonth, 0)

    let html = ''

    // 이전 달 날짜
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      const day = prevMonth.getDate() - i
      html += `<button type="button" class="w-10 h-10 text-sm text-gray-300 rounded-lg" disabled>${day}</button>`
    }

    // 현재 달 날짜
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(this.viewYear, this.viewMonth, day)
      const dateStr = this.formatDate(date)
      const isToday = this.isToday(date)
      const isSelected = this.selectedDate && this.formatDate(this.selectedDate) === dateStr
      const dayOfWeek = date.getDay()

      let classes = 'w-10 h-10 text-sm rounded-lg font-medium transition-colors '

      if (isSelected) {
        classes += 'bg-blue-500 text-white '
      } else if (isToday) {
        classes += 'bg-blue-50 text-blue-600 border border-blue-200 '
      } else {
        classes += 'hover:bg-gray-100 '
        if (dayOfWeek === 0) {
          classes += 'text-red-500 '
        } else if (dayOfWeek === 6) {
          classes += 'text-blue-500 '
        } else {
          classes += 'text-gray-700 '
        }
      }

      html += `<button type="button" class="${classes}" data-action="click->date-picker-modal#selectDate" data-date="${dateStr}">${day}</button>`
    }

    // 다음 달 날짜 (6주 채우기)
    const totalCells = prevMonthDays + lastDay.getDate()
    const remainingCells = totalCells > 35 ? 42 - totalCells : 35 - totalCells

    for (let day = 1; day <= remainingCells; day++) {
      html += `<button type="button" class="w-10 h-10 text-sm text-gray-300 rounded-lg" disabled>${day}</button>`
    }

    calendar.innerHTML = html
  }

  prevMonth() {
    this.viewMonth--
    if (this.viewMonth < 0) {
      this.viewMonth = 11
      this.viewYear--
    }
    this.renderCalendar()
  }

  nextMonth() {
    this.viewMonth++
    if (this.viewMonth > 11) {
      this.viewMonth = 0
      this.viewYear++
    }
    this.renderCalendar()
  }

  changeYear(event) {
    this.viewYear = parseInt(event.target.value)
    this.renderCalendar()
  }

  changeMonth(event) {
    this.viewMonth = parseInt(event.target.value)
    this.renderCalendar()
  }

  selectDate(event) {
    const dateStr = event.currentTarget.dataset.date
    this.selectedDate = new Date(dateStr)
    this.inputTarget.value = dateStr
    this.updateDisplay()
    this.closeModal()

    // Change 이벤트 발생
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  selectToday() {
    this.selectQuickDate(0)
  }

  selectTomorrow() {
    this.selectQuickDate(1)
  }

  selectDayAfter() {
    this.selectQuickDate(2)
  }

  selectQuickDate(daysFromToday) {
    const date = new Date()
    date.setDate(date.getDate() + daysFromToday)
    this.selectedDate = date
    this.viewYear = date.getFullYear()
    this.viewMonth = date.getMonth()
    this.inputTarget.value = this.formatDate(date)
    this.updateDisplay()
    this.closeModal()

    // Change 이벤트 발생
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  clearDate() {
    this.selectedDate = null
    this.inputTarget.value = ''
    this.updateDisplay()
    this.closeModal()

    // Change 이벤트 발생
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  updateDisplay() {
    if (this.hasDisplayTarget) {
      if (this.selectedDate) {
        this.displayTarget.textContent = this.formatDisplayDate(this.selectedDate)
        this.displayTarget.classList.remove('text-gray-400')
        this.displayTarget.classList.add('text-gray-900')
      } else {
        this.displayTarget.textContent = '날짜를 선택하세요'
        this.displayTarget.classList.add('text-gray-400')
        this.displayTarget.classList.remove('text-gray-900')
      }
    }
  }

  formatDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  formatDisplayDate(date) {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()]
    return `${year}년 ${month}월 ${day}일 (${dayOfWeek})`
  }

  isToday(date) {
    const today = new Date()
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
}
